export type ShipmentIdentifierType =
  | 'bol'
  | 'shipmentnumber'
  | 'ordernumber'
  | 'customerpo'
  | 'webordernumber';

export const VALID_TRACKING_MODES: ShipmentIdentifierType[] = [
  'bol', 'shipmentnumber', 'ordernumber', 'customerpo', 'webordernumber'
];

export function isValidTrackingMode(mode: string): mode is ShipmentIdentifierType {
  return VALID_TRACKING_MODES.includes(mode as ShipmentIdentifierType);
}

export const TRACKING_PLACEHOLDERS: Record<ShipmentIdentifierType, string> = {
  'bol': 'e.g. 12239716, 05 12239716, or 12239716-05',
  'shipmentnumber': 'e.g. 12239716',
  'ordernumber': 'e.g. 24115350 or SO24115350',
  'customerpo': 'e.g. 92813-JJF',
  'webordernumber': 'e.g. WEB20250619278989100'
};

export interface ShipmentValidationContext {
  orderType?: 'CPP' | 'seed';
  customerName?: string;
}

export interface ShipmentValidationResult {
  isValid: boolean;
  normalizedValue?: string;
  error?: string;
  parsed?: {
    stopCode?: string;
    shipmentNumber?: string;
    orderType?: string;
    orderNumber?: string;
    webOrderNumber?: string;
    splitId?: string;
  };
}

export interface TrackingValidation {
  isSendDisabled: boolean;
  validationError: string;
  result: ShipmentValidationResult | null;
}

export function getTrackingValidation(
  userInput: string,
  isTrackingFlowActive: boolean,
  currentTrackingMode: string,
  context?: ShipmentValidationContext
): TrackingValidation {
  const trimmed = (userInput || '').trim();
  if (!trimmed) {
    return { isSendDisabled: true, validationError: '', result: null };
  }
  if (isTrackingFlowActive && currentTrackingMode && isValidTrackingMode(currentTrackingMode)) {
    const result = validateShipmentIdentifier(currentTrackingMode, trimmed, context);
    return {
      isSendDisabled: !result.isValid,
      validationError: result.error || '',
      result
    };
  }
  return { isSendDisabled: false, validationError: '', result: null };
}

export function validateShipmentIdentifier(
  identifierType: ShipmentIdentifierType,
  value: string,
  context?: ShipmentValidationContext
): ShipmentValidationResult {
  if (!value || !value.trim()) {
    return { isValid: false, error: 'Identifier is required.' };
  }

  const trimmed = value.trim();

  switch (identifierType) {
    case 'bol':
      return validateBol(trimmed);

    case 'shipmentnumber':
      return validateShipmentNumber(trimmed);

    case 'ordernumber':
      return validateOrderNumber(trimmed);

    case 'customerpo':
      return validateCustomerPo(trimmed);

    case 'webordernumber':
      return validateWebReferenceNumber(trimmed);

    default:
      return { isValid: false, error: 'Unknown identifier type.' };
  }
}

function validateBol(value: string): ShipmentValidationResult {
  if (value.length >= 1 && value.length <= 25) {
    return { isValid: true, normalizedValue: value };
  }

  return { isValid: false, error: 'BOL Number must be 25 characters or fewer.' };
}

function validateShipmentNumber(value: string): ShipmentValidationResult {
  if (value.length >= 1 && value.length <= 25) {
    return { isValid: true, normalizedValue: value };
  }

  return { isValid: false, error: 'ShipmentNumber must be 25 characters or fewer.' };
}

function validateOrderNumber(value: string): ShipmentValidationResult {
  const match = value.match(/^(?:(SO)\s?)?(\d{8})$/);
  if (match) {
    return {
      isValid: true,
      normalizedValue: match[2],
      parsed: { orderType: 'SO', orderNumber: match[2] }
    };
  }

  return { isValid: false, error: 'Order number must be 8 numeric digits, optionally prefixed with SO.' };
}

function validateCustomerPo(value: string): ShipmentValidationResult {
  if (value.length >= 1 && value.length <= 25) {
    return { isValid: true, normalizedValue: value };
  }

  return { isValid: false, error: 'Customer PO must be 25 characters or fewer.' };
}

// function validateWebReferenceNumber(value: string): ShipmentValidationResult {
//   const match = value.match(/^WEB(\d{14})(\d{3})$/i);
//   if (match) {
//     return {
//       isValid: true,
//       normalizedValue: value.toUpperCase(),
//       parsed: { webOrderNumber: match[1], splitId: match[2] }
//     };
//   }

//   return { isValid: false, error: 'Web Reference Number must be WEB followed by exactly 30 digits.' };
// }
function validateWebReferenceNumber(value: string): ShipmentValidationResult {
  const trimmed = value.trim();

  const isValid = /^[A-Za-z0-9]{1,30}$/.test(trimmed);
  if (isValid) {
    return { isValid: true, normalizedValue: trimmed.toUpperCase() };
  }

  return {
    isValid: false,
    error: 'Web Reference Number must be alphanumeric and 30 characters or fewer.'
  };
}
